import React from 'react'
import PropTypes from 'prop-types'
import compose from '../../../utils/functions'

// Apollo client
import { withApollo } from '@apollo/client/react/hoc'
import { cancelAppointment, confirmAppointment, confirmAppointmentWithAppointmentSheet, getAppointmentById } from '../../../graphql/appointment'
import { generateReadSignedURL, generateUploadSignedURL } from '../../../graphql/uploadFiles'
import { getNeedAppointmentSheetForLoggedSocialWorker } from '../../../graphql/social_workers'
import { v4 as uuidv4 } from 'uuid'

// Components imports
import HistoryIcon from '@material-ui/icons/History'

// Design imports
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    Paper,
    Typography,
} from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import { APPOINTMENT_PREFERENCE_API } from '../../../constants'
import moment from 'moment/moment'
import styles from './styles'
import DownloadAppointmentPaper from '../../../components/downloadAppointmentPaper/DownloadAppointmentPaper'
import SnackbarSuccess from '../../../components/molecule/SnackbarSuccess/SnackbarSuccess'

// Constants

class AppointmentDetail extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            notification: {},
            deleteDialogOpened: false,
            confirmSnackbarOpened: false,
            deleteSnackbarOpened: false,
            modifyDialogOpened: false,
            // New state for appointment sheet dialog
            appointmentSheetDialogOpened: false,
            appointmentSheetFile: null,
            appointmentSheetError: false,
            needAppointmentSheet: false,
            isUploadingFile: false,
        }
    }

    parseData = (appointmentFromAPI) => {
        var favoriteTime = ''
        switch (appointmentFromAPI.preference) {
            case APPOINTMENT_PREFERENCE_API.MORNING:
                favoriteTime = 'Mati'
                break
            case APPOINTMENT_PREFERENCE_API.AFTERNOON:
                favoriteTime = 'Tarda'
                break
            default:
                favoriteTime = 'Indiferent'
        }

        var proposedDate =
            appointmentFromAPI.proposedDate !== null
                ? appointmentFromAPI.proposedHour +
                  ' - ' +
                  moment(parseInt(appointmentFromAPI.proposedDate)).format('DD/MM/YYYY')
                : ''

        const appointment = {
            id: appointmentFromAPI.id,
            name:
                appointmentFromAPI.patient?.name?.toUpperCase() +
                ' ' +
                appointmentFromAPI.patient?.lastName?.toUpperCase(),
            patient: appointmentFromAPI.patient,
            phone: appointmentFromAPI.patient.phone,
            birthdate: appointmentFromAPI.patient.birthdate,
            entity: appointmentFromAPI.patient?.entity?.name?.toUpperCase(),
            favoriteTime: favoriteTime,
            observations: appointmentFromAPI.observations ? appointmentFromAPI.observations : '-',
            proposedDate: proposedDate,
            confirmed: appointmentFromAPI.confirmed,
            haveProposedDate: appointmentFromAPI.proposedDate !== '' && appointmentFromAPI.proposedDate !== null,
            createdByUser: appointmentFromAPI.createdByUser,
            appointmentSheet: appointmentFromAPI.appointmentSheet,
        }

        this.setState({ notification: appointment })
    }

    fetchAppointment = () => {
        this.props.client
            .query(getAppointmentById(this.props.match.params.id))
            .then((result) => this.parseData(result.data.appointment))
    }

    fetchNeedAppointmentSheet = () => {
        this.props.client.query(getNeedAppointmentSheetForLoggedSocialWorker()).then((result) => {
            this.setState({
                needAppointmentSheet: result.data.getNeedAppointmentSheetForLoggedSocialWorker.needAppointmentSheet,
            })
        })
    }

    componentDidMount() {
        this.fetchAppointment()
        this.fetchNeedAppointmentSheet()
    }

    goToNewAppointment = () => {
        const patient = this.state.notification.patient
        this.props.history.push({
            pathname: '/new-appointment',
            state: { id: patient.id, name: patient.name, lastName: patient.lastName, prevPath: this.props.match.path },
        })
    }

    handleConfirmButtonClick = () => {
        // Check if appointment sheet is required
        if (this.state.needAppointmentSheet) {
            // Show dialog to upload appointment sheet
            this.setState({ appointmentSheetDialogOpened: true })
        } else {
            // Proceed with normal confirmation
            this.handleNormalConfirmation()
        }
    }

    handleNormalConfirmation = () => {
        const mutation = confirmAppointment(this.props.match.params.id)

        this.props.client.mutate(mutation).then((result) => {
            this.setState({ confirmSnackbarOpened: true })
            this.fetchAppointment()
        })
    }

    handleFileChange = (event) => {
        const file = event.target.files[0]
        if (
            file &&
            (file.type === 'application/pdf' ||
                file.type === 'application/msword' ||
                file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
        ) {
            this.setState({ appointmentSheetFile: file, appointmentSheetError: false })
        } else {
            this.setState({ appointmentSheetFile: null, appointmentSheetError: true })
        }
    }

    handleConfirmWithAppointmentSheet = async () => {
        const { appointmentSheetFile } = this.state

        if (!appointmentSheetFile) {
            this.setState({ appointmentSheetError: true })
            return
        }

        this.setState({ isUploadingFile: true })

        try {
            // Generar nombre único con UUID y mantener la extensión
            const ext = appointmentSheetFile.name.split('.').pop()
            const uniqueFileName = `${uuidv4()}.${ext}`

            // Crear nuevo objeto File con el nombre único
            let fileToUpload
            try {
                fileToUpload = new File([appointmentSheetFile], uniqueFileName, { type: appointmentSheetFile.type })
            } catch (e) {
                // Si falla, usar el original
                fileToUpload = appointmentSheetFile
            }

            // 1. Pedir signed URL para subida
            const { type } = fileToUpload
            const mutation = generateUploadSignedURL(uniqueFileName, type)
            const response = await this.props.client.mutate(mutation)
            const signedUrl = response.data.generateUploadSignedURL

            if (!signedUrl) {
                throw new Error('No se pudo obtener la URL firmada para la subida')
            }

            // 2. Subir el archivo a S3 usando fetch
            const uploadRes = await fetch(signedUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': type,
                },
                body: fileToUpload,
            })

            if (!uploadRes.ok) {
                throw new Error('Error al subir el archivo a S3')
            }

            // 3. Confirmar la cita con el archivo adjunto
            const confirmMutation = confirmAppointmentWithAppointmentSheet(this.props.match.params.id, uniqueFileName)
            await this.props.client.mutate(confirmMutation)

            // Confirmación exitosa
            this.setState({
                confirmSnackbarOpened: true,
                appointmentSheetDialogOpened: false,
                isUploadingFile: false
            })
            this.fetchAppointment()

        } catch (error) {
            console.error('Error al subir el archivo:', error)
            this.setState({
                appointmentSheetError: true,
                isUploadingFile: false
            })
            alert('Error al subir el archivo: ' + error.message)
        }
    }

    handleDeleteButtonClick = () => {
        this.setState({ deleteDialogOpened: true })
    }

    handleClose = () => {
        this.setState({
            deleteDialogOpened: false,
            modifyDialogOpened: false,
            confirmSnackbarOpened: false,
            deleteSnackbarOpened: false,
            appointmentSheetDialogOpened: false,
            appointmentSheetError: false,
        })
    }

    handleConfirmDelete = () => {
        this.handleClose()
        const mutation = cancelAppointment(this.props.match.params.id)

        this.props.client.mutate(mutation).then((result) => {
            const incidenceId = result.data.cancelAppointment.incidenceId
            this.setState({ deleteSnackbarOpened: true })
            setTimeout(() => {
                this.props.history.push('/incidence/' + incidenceId)
            }, 3000)
        })
    }

    handleDownloadAppointmentSheet = async () => {
        const { notification } = this.state
        if (!notification.appointmentSheet) return
        try {
            // 1. Obtener la URL firmada
            const result = await this.props.client.query(generateReadSignedURL(notification.appointmentSheet))
            const url = result.data.generateReadSignedURL
            if (!url) throw new Error('No se pudo obtener la URL de descarga.')

            // 2. Descargar el archivo
            const response = await fetch(url)
            if (!response.ok) throw new Error('Error al descargar el archivo.')
            const blob = await response.blob()

            // 3. Crear un enlace y forzar la descarga
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = notification.appointmentSheet
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            console.error('Error al descargar el archivo:', error)
        }
    }

    handleModifyAppointment = () => {
        this.handleClose()
        const mutation = cancelAppointment(this.props.match.params.id)

        this.props.client.mutate(mutation).then((result) => {
            this.goToNewAppointment()
        })
    }

    renderActionButtons = () => {
        const { classes } = this.props
        const { notification } = this.state

        if (notification.haveProposedDate && notification.confirmed === false) {
            return (
                <div className={classes.cardFooter}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="large"
                        className={classes.actionButton}
                        onClick={() => this.setState({ modifyDialogOpened: true })}>
                        Obrir incidència
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        className={classes.actionButton}
                        onClick={() => this.handleConfirmButtonClick()}>
                        Confirmar
                    </Button>
                </div>
            )
        } else if (notification.haveProposedDate && notification.confirmed === true) {
            return (
                <div className={classes.cardFooter}>
                    <DownloadAppointmentPaper
                        entityName={notification?.entity || ''}
                        fullName={notification?.name || ''}
                        age={
                            moment()
                                .diff(moment(parseInt(notification?.birthdate)), 'years')
                                .toString() || ''
                        }
                        appointmentDate={notification?.proposedDate.split(' - ')[1] || ''}
                        appointmentTime={notification?.proposedDate.split(' - ')[0] || ''}
                        socialWorker={
                            notification?.createdByUser
                                ? notification?.createdByUser?.name?.toUpperCase() +
                                  ' ' +
                                  notification?.createdByUser?.lastName?.toUpperCase()
                                : ''
                        }
                    />
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="large"
                        className={classes.actionButton}
                        onClick={() => this.setState({ modifyDialogOpened: true })}>
                        Obrir incidència
                    </Button>
                </div>
            )
        } else {
            return (
                <div className={classes.cardFooter}>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="large"
                        className={classes.actionButton}
                        onClick={() => this.handleDeleteButtonClick()}>
                        Cancel·lar visita
                    </Button>
                </div>
            )
        }
    }

    render() {
        const { classes } = this.props
        const { notification, appointmentSheetFile, appointmentSheetError, isUploadingFile } = this.state

        return (
            <div className={classes.root}>
                <Grid container spacing={24}>
                    <Grid item xs={12} sm={12} md={8} lg={6}>
                        <Paper className={classes.paperRoot}>
                            <div className={classes.cardHeader}>
                                <div className={classes.cardTitleContainer}>
                                    <HistoryIcon color="primary" style={{ fontSize: 28 }} />
                                    <Typography className={classes.title} variant="h6" color="primary">
                                        Detalls de la petició de visita
                                    </Typography>
                                </div>
                            </div>
                            <Divider />
                            <div className={classes.cardContainer}>
                                <Grid container spacing={16}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography className={classes.labelTitle} variant="subtitle2">
                                            Data i hora proposada:
                                        </Typography>
                                        <Typography className={classes.label} variant="h6">
                                            {notification.proposedDate !== '' ? notification.proposedDate : '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography className={classes.labelTitle} variant="subtitle2">
                                            Visita confirmada:
                                        </Typography>
                                        <Typography className={classes.label} variant="h6">
                                            {notification.confirmed ? 'Sí' : 'No'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography className={classes.labelTitle} variant="subtitle2">
                                            Pacient:
                                        </Typography>
                                        <Typography className={classes.label} variant="h6">
                                            {notification.name}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography className={classes.labelTitle} variant="subtitle2">
                                            Telèfon:
                                        </Typography>
                                        <Typography className={classes.label} variant="h6">
                                            {notification.phone}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography className={classes.labelTitle} variant="subtitle2">
                                            Preferència:
                                        </Typography>
                                        <Typography className={classes.label} variant="h6">
                                            {notification.favoriteTime}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <Typography className={classes.labelTitle} variant="subtitle2">
                                            Observacions:
                                        </Typography>
                                        <Typography className={classes.label} variant="h6">
                                            {notification.observations}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <Typography className={classes.labelTitle} variant="subtitle2">
                                            Treballador social:
                                        </Typography>
                                        <Typography className={classes.label} variant="h6">
                                            {notification.createdByUser !== null &&
                                            notification.createdByUser !== undefined
                                                ? notification.createdByUser.name?.toUpperCase() +
                                                  ' ' +
                                                  notification.createdByUser.lastName?.toUpperCase()
                                                : '-'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </div>
                            <Divider />
                            {this.renderActionButtons()}
                        </Paper>
                    </Grid>
                    {notification.appointmentSheet && (
                        <Grid item xs={12} sm={12} md={8} lg={6}>
                            <Paper className={classes.paperRoot}>
                                <div className={classes.cardHeader}>
                                    <div className={classes.cardTitleContainer}>
                                        <Typography variant="h6" color="primary">
                                            Documentació de la visita
                                        </Typography>
                                    </div>
                                </div>
                                <Divider />
                                <div className={classes.cardContainer}>
                                    <Grid container spacing={16}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography className={classes.labelTitlePadding} variant="subtitle2">
                                                Fulla de derivació:
                                            </Typography>
                                            <Button
                                                onClick={this.handleDownloadAppointmentSheet}
                                                color="primary"
                                                variant={'outlined'}
                                                autoFocus>
                                                Descarregar fulla
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </div>
                            </Paper>
                        </Grid>
                    )}
                </Grid>

                {/* Existing Dialogs */}
                <Dialog
                    open={this.state.deleteDialogOpened}
                    onClose={this.handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description">
                    <DialogTitle id="alert-dialog-title">{'Està segur que vol cancel·lar la visita?'}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Un cop cancel·lada la visita, s'obrirà una incidència on podrà demanar una nova hora o
                            indicar perquè s'ha cancel·lat la visita. Si té algun dubte pot trucar al 937 398 349.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleClose} color="secondary">
                            No
                        </Button>
                        <Button onClick={this.handleConfirmDelete} color="primary" autoFocus>
                            Sí, cancelar visita
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={this.state.modifyDialogOpened}
                    onClose={this.handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description">
                    <DialogTitle id="alert-dialog-title">{'Està segur que vol obrir una nova incidència?'}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Si obre una nova incidència, la visita quedarà cancel·lada automàticament. Podrà demanar una
                            nova visita a través d'aquesta incidència o bé cursant una nova petició a través de
                            l'aplicatiu. Si té algun dubte pot trucar al 937 398 349.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleClose} color="secondary">
                            No
                        </Button>
                        <Button onClick={this.handleConfirmDelete} color="primary" autoFocus>
                            Sí, obrir incidència
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* New Appointment Sheet Dialog */}
                <Dialog
                    open={this.state.appointmentSheetDialogOpened}
                    onClose={this.handleClose}
                    aria-labelledby="appointment-sheet-dialog-title"
                    maxWidth="sm"
                    fullWidth>
                    <DialogTitle id="appointment-sheet-dialog-title">
                        Confirmar visita - Fulla de derivació requerida
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText style={{ marginBottom: 16 }}>
                            La vostra entitat requereix que s'adjunti una fulla de derivació per confirmar aquesta visita.
                            Si us plau, seleccioneu el document i procediu amb la confirmació.
                        </DialogContentText>

                        <div style={{ marginBottom: 16 }}>
                            <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 'bold' }}>
                                Adjunti la fulla de derivació del pacient:
                            </Typography>

                            <input
                                accept=".pdf,.doc,.docx"
                                style={{ display: 'none' }}
                                id="appointment-sheet-upload"
                                type="file"
                                onChange={this.handleFileChange}
                            />
                            <label htmlFor="appointment-sheet-upload">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component="span"
                                    disabled={isUploadingFile}>
                                    Seleccionar arxiu
                                </Button>
                            </label>

                            {appointmentSheetFile && (
                                <Typography variant="body2" style={{ marginTop: 8, color: '#4caf50' }}>
                                    ✓ Fitxer seleccionat: {appointmentSheetFile.name}
                                </Typography>
                            )}

                            {appointmentSheetError && (
                                <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                                    Error: El fitxer pujat ha de ser un document en format PDF, DOC o DOCX.
                                </Typography>
                            )}
                        </div>

                        {/* Patient and appointment details */}
                        <div style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 4 }}>
                            <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 'bold' }}>
                                Detalls de la visita:
                            </Typography>
                            <Typography variant="body2"><strong>Pacient:</strong> {notification.name}</Typography>
                            <Typography variant="body2"><strong>Data i hora:</strong> {notification.proposedDate}</Typography>
                            <Typography variant="body2"><strong>Preferència:</strong> {notification.favoriteTime}</Typography>
                            {notification.observations !== '-' && (
                                <Typography variant="body2"><strong>Observacions:</strong> {notification.observations}</Typography>
                            )}
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleClose} color="secondary" disabled={isUploadingFile}>
                            Cancel·lar
                        </Button>
                        <Button
                            onClick={this.handleConfirmWithAppointmentSheet}
                            color="primary"
                            variant="contained"
                            disabled={!appointmentSheetFile || isUploadingFile}>
                            {isUploadingFile ? 'Processant...' : 'Confirmar visita'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbars */}
                <SnackbarSuccess
                    open={this.state.confirmSnackbarOpened}
                    title={'Visita confirmada correctament'}
                    onClose={this.handleClose}
                />
                <SnackbarSuccess
                    open={this.state.deleteSnackbarOpened}
                    title={'Visita cancel·lada correctament'}
                    onClose={this.handleClose}
                />
            </div>
        )
    }
}

AppointmentDetail.propTypes = {
    classes: PropTypes.object.isRequired,
}

AppointmentDetail.defaultProps = {}

export default compose(withApollo, withStyles(styles))(AppointmentDetail)
